// Un importe que se TIPEA en el mostrador, con sus centavos.
//
// El modelo era «dígitos = pesos enteros» (`Number(digitos) * 100`), y eso
// alcanza para una verdulería donde no hay monedas… hasta que no: el cobro sin
// detalle registra el importe que ya calculó OTRO equipo —la balanza
// etiquetadora—, y ese ticket dice $1.487,50. Sin coma, el cajero tiene que
// elegir entre cobrar de más o de menos, y el arqueo del turno cierra con una
// diferencia que después nadie puede explicar. Y peor que la falta: apretar la
// coma del numpad no hacía NADA, así que la pantalla no decía que no se podía,
// simplemente ignoraba la tecla.
//
// Se maneja como TEXTO y no como número, por lo mismo que el diálogo de
// cantidad: es un teclado de calculadora. Con un número de por medio, «1500,»
// —el estado en el que la persona ya apretó la coma y todavía no tipeó el
// primer centavo— no se puede representar, y es exactamente el estado que hay
// que poder mostrar para que la coma se vea.
//
// La coma es el separador porque es el de es-AR y es lo que se ve en pantalla.
// El punto del teclado numérico se acepta igual: en el numpad de una PC la
// tecla dice «.» y nadie va a buscar la coma en otra parte.

/** Cuántos decimales admite un importe. Son centavos: dos y no más. */
const DECIMALES = 2

/** Cuántos dígitos admite la parte entera. Nueve son mil millones de pesos. */
const ENTEROS = 9

/** Las teclas que este modelo entiende, para que el listener no las adivine. */
export function esTeclaDeImporte(tecla: string): boolean {
  return /^[0-9]$/.test(tecla) || tecla === ',' || tecla === '.'
}

/**
 * Agrega una tecla a lo tipeado. Devuelve lo mismo si la tecla no entra.
 *
 * Que devuelva lo MISMO y no null es lo que deja escribirlo como
 * `setDigitos((v) => tipear(v, tecla))`: el que llama no tiene que decidir
 * nada, y React no re-renderiza si el estado no cambió.
 */
export function tipear(actual: string, tecla: string): string {
  if (tecla === ',' || tecla === '.') {
    // Una sola coma, y con la parte entera en cero si todavía no hay nada:
    // «,50» se lee mal y «0,50» es lo que el cajero esperaba escribir.
    if (actual.includes(',')) return actual
    return actual === '' ? '0,' : `${actual},`
  }

  if (!/^[0-9]$/.test(tecla)) return actual

  const coma = actual.indexOf(',')

  if (coma === -1) {
    // Sin coma: se está cargando la parte entera. El cero solo a la izquierda
    // no se acumula («0007» no es un importe que nadie quiso escribir).
    if (actual === '0') return tecla
    return actual.length >= ENTEROS ? actual : actual + tecla
  }

  const decimales = actual.length - coma - 1
  return decimales >= DECIMALES ? actual : actual + tecla
}

/** Borra el último caracter, coma incluida. */
export function borrarUno(actual: string): string {
  return actual.slice(0, -1)
}

/**
 * Los centavos de lo tipeado.
 *
 * Multiplica ANTES de dividir y redondea, que es la misma precaución del
 * gemelo de la aritmética de la venta: `Number('0.1') * 100` no da 10 exacto
 * en punto flotante, y un centavo de diferencia acá es un cobro rechazado por
 * el servidor con el cliente adelante.
 */
export function centavosTipeados(actual: string): number {
  if (actual === '' || actual === ',') return 0

  const [enteros = '', decimales = ''] = actual.split(',')
  const centavos = (decimales + '00').slice(0, DECIMALES)

  return Number(enteros || '0') * 100 + Number(centavos)
}

/**
 * Lo tipeado como texto de entrada de un campo editable: sin símbolo y sin
 * separador de miles.
 *
 * Es para el «¿con cuánto paga?», que es un input de verdad y no un display:
 * meterle los puntos de miles mientras se escribe pelea con el cursor.
 */
export function textoDeCentavos(centavos: number): string {
  if (centavos <= 0) return ''

  const enteros = Math.floor(centavos / 100)
  const resto = centavos % 100

  return resto === 0 ? String(enteros) : `${enteros},${String(resto).padStart(2, '0')}`
}

/**
 * Lo que se tipeó (o se pegó) en un campo editable, quedándose solo con lo que
 * es importe.
 *
 * El punto de MILES se saca antes, y hay que distinguirlo del decimal porque
 * los dos son el mismo caracter: pegar «1.487,50» —que es como el sistema
 * muestra la plata en todas las demás pantallas— leído con el punto como coma
 * daba «1,48», o sea un peso cuarenta y ocho en lugar de mil cuatrocientos
 * ochenta y siete. La regla es la forma: un punto seguido de exactamente tres
 * dígitos y nada más que sea dígito es un separador de miles; con uno o dos
 * dígitos atrás es el decimal del numpad.
 */
export function normalizarTipeado(texto: string): string {
  const sinMiles = texto.replace(/\.(?=\d{3}(?!\d))/g, '')

  let salida = ''

  for (const caracter of sinMiles) {
    salida = tipear(salida, caracter)
  }

  return salida
}
